import type { Session } from '@setplex/tria-api'
import type { SemVer } from '~/shared/tools/semver'

import { api } from '@setplex/tria-api'
import { createEffect, createStore, sample } from 'effector'
import { logger } from '~/shared/logger'
import { lt, parse } from '~/shared/tools/semver'
import { $featuredFlags, set } from './features'

// full wbs version from session
// only set once, because application works with single wbs instance,
// and version cannot be changed even if user logs out and logs in again
export const $wbsRawVersion = createStore<string | null>(null)

sample({
  clock: api.session.$session,
  filter: (session: Session | null): session is Session =>
    session !== null && session.version !== null,
  fn: (session) => session.version ?? null,
  target: $wbsRawVersion,
})

const parseFx = createEffect(parse)

// parsed version
export const $wbsVersion = createStore<SemVer | null>(null)

sample({
  clock: $wbsRawVersion,
  filter: (version) => version !== null,
  target: parseFx,
})

sample({
  clock: parseFx.doneData,
  target: $wbsVersion,
})

sample({
  clock: parseFx.failData,
  fn: (fail) => ['failed to parse version:', fail],
  target: logger.errorFx,
})

/** NoraMW ≥ 3.24: backend-localized content metadata / navigation (FP-4184) */
export const CONTENT_LOCALIZATION_MIN_VERSION: SemVer = [3, 24, 0]

export function isContentLocalizationEnabled(version: SemVer | null): boolean {
  return version !== null && !lt(version, CONTENT_LOCALIZATION_MIN_VERSION)
}

export const $isContentLocalizationEnabled = $wbsVersion.map(
  isContentLocalizationEnabled
)

/**
 * NoraMW ≥ 3.24 (major+minor only, patch ignored): `GET /v3/search/hybrid/sections`
 * with EPG data (FP-4249).
 *
 * The endpoint first shipped in `@bldsoft/wbs-api-types` 3.24.3, but a deployed WBS
 * build's own patch number does not reliably track that library version (e.g. a
 * build reporting `3.24.0#<hash>` can already serve the endpoint). Comparing the
 * patch would incorrectly keep the fallback flat-search path on such builds, so
 * only major+minor are compared.
 */
export const SECTIONED_SEARCH_MIN_VERSION: SemVer = [3, 24]

export function isSectionedSearchEnabled(version: SemVer | null): boolean {
  return (
    version !== null &&
    !lt(
      [version[0], version[1]],
      [SECTIONED_SEARCH_MIN_VERSION[0], SECTIONED_SEARCH_MIN_VERSION[1]]
    )
  )
}

export const $isSectionedSearchEnabled = $wbsVersion.map(
  isSectionedSearchEnabled
)

/*
 * Adjust flags, features and options depending on version
 * Version parsing and adjusting behaviour from the version is ad hoc solution for specific cases,
 * and we need more general and robust approach, which should be implemented in future
 */

// remove feature `appearanceCustomizationEnabled` in case version < 3.3
// see more about this issue - https://bldsoft.slack.com/archives/C02LLFVSJQ0/p1727791152684229
// after version 3.3 will be deployed everywhere, feature flag `appearanceCustomizationEnabled` will be permanently `true`,
// so we will be able to remove this flag from features at all later
sample({
  source: { version: $wbsVersion, features: $featuredFlags },
  filter: ({ version, features }) =>
    version !== null &&
    lt(version, [3, 3, 0]) && // version < 3.3
    features.appearanceCustomizationEnabled != null, // feature exists and set to true or false
  fn: () => ({ appearanceCustomizationEnabled: undefined }), // erase it
  target: set,
})
